import { useMemo } from 'react';

import { canAccessCustomersNav } from '@/features/anken/utils/resolveEntryPath';
import { useRealtorSidebarItems } from '@/hooks/useRealtorSidebarItems';
import { hasChatWriteAccess } from '@/utils';

type HomeAction = {
  label: string;
  href: string;
};

// ホームは withAuthRequired だけで全ログインユーザーに出るため、表示判定はサイドバーと同じ
// 「事業者の契約（enable_*）× ユーザーの能力」の2軸に揃える（useRealtorSidebarItems.tsx:157 の方針）。
// ロールだけで判定すると、サイドバーに出していない画面へホームからだけ入れてしまう。
export const useHomeActions = () => {
  const { me, orgInfo, showAnkenContract, showBaikaiTab, hasChatMembership } = useRealtorSidebarItems();

  const roles = me?.info?.roles;
  const enableAnken = Boolean(orgInfo?.enableAnken);
  const enableChat = Boolean(orgInfo?.enableChat);

  return useMemo(() => {
    if (!roles) return [];

    // 顧客はサイドバーと同じ述語を使う。
    const showCustomers = canAccessCustomersNav(enableAnken, enableChat, roles);
    // チャット「作成」は write 権限が必須。hasChatAccess は read のみの EXTERNAL_CHAT_PARTICIPANT も
    // true になり、これは全ユーザーに既定で付与されるため write 系 UI の出し分けには使えない。
    // 到達性は入口側（resolveEntryPath）と同じく enableChat || membership で判定する。
    const canCreateChat = hasChatWriteAccess(roles) && (enableChat || hasChatMembership);

    // 在庫・媒介がサイドバーに出ている事業者は在庫一覧へ、それ以外は契約案件一覧へ送る。
    // どちらも出ていないユーザーには案件系のアクションを出さない。
    const ankenBasePath = showBaikaiTab ? '/anken/ryutsu' : showAnkenContract ? '/anken/app' : undefined;

    const list: HomeAction[] = [];
    if (ankenBasePath) {
      list.push({
        label: '担当案件を見る',
        href: `${ankenBasePath}?pic=me`,
      });
    }
    if (showCustomers) {
      list.push({
        label: '顧客を登録',
        href: '/anken/customers?new=customer',
      });
    }
    if (ankenBasePath) {
      list.push({
        label: '案件を登録',
        href: ankenBasePath === '/anken/ryutsu' ? '/anken/ryutsu?new=ryutsu' : '/anken/app?new=contract',
      });
    }
    if (canCreateChat) {
      list.push({
        label: 'チャットを作成',
        href: '/anken/chat?new=chat',
      });
    }
    return list;
  }, [roles, enableAnken, enableChat, showAnkenContract, showBaikaiTab, hasChatMembership]);
};
